import dayjs from 'dayjs';
import { FileStatus } from 'store/fileState/types';
import type { StorageUnit } from './types';
import { StorageUnitStatus } from './types';

export function toIsoString(value?: Date | string | null): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return value.toISOString();
  } catch (_err) {
    return new Date(value).toISOString();
  }
}

export function toTimestamp(value?: Date | string | null): number | null {
  const iso = toIsoString(value);
  if (!iso) {
    return null;
  }

  const timestamp = new Date(iso).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export type LeaseEvaluation = {
  status: FileStatus;
  exclusiveByOther: boolean;
  softByOther: boolean;
};

export const evaluateLeaseState = (
  unit: StorageUnit | null,
  fallbackStatus: FileStatus,
  currentUserId?: string | null,
): LeaseEvaluation => {
  const result: LeaseEvaluation = {
    status: fallbackStatus,
    exclusiveByOther: false,
    softByOther: false,
  };

  if (!unit) {
    return result;
  }

  if (unit.status.includes(StorageUnitStatus.TEMPLATE)) {
    result.status = FileStatus.TEMPLATE;
    return result;
  }

  const exclusiveLease = unit.exclusiveLease;
  const softLeases = unit.softLeases ?? [];

  if (exclusiveLease) {
    const exclusiveHeldByOther =
      !currentUserId || exclusiveLease.userId !== currentUserId;
    if (exclusiveHeldByOther) {
      result.status = FileStatus.READ_ONLY;
      result.exclusiveByOther = true;
      return result;
    }
  }

  if (
    softLeases.some((lease) => !currentUserId || lease.userId !== currentUserId)
  ) {
    result.softByOther = true;
  }

  return result;
};

export const sortByName = (u1: StorageUnit, u2: StorageUnit) =>
  u1.name.localeCompare(u2.name);

export const sortByRecent = (u1: StorageUnit, u2: StorageUnit) =>
  dayjs(u1.updatedAt ?? u1.createdAt).isBefore(
    dayjs(u2.updatedAt ?? u2.createdAt),
  )
    ? 0
    : 1;

export const normalizeLeases = (unit: StorageUnit): StorageUnit => {
  const normalizedAccess = unit.access ?? { direct: [], indirect: [] };
  return {
    ...unit,
    access: {
      direct: [...normalizedAccess.direct],
      indirect: [...normalizedAccess.indirect],
    },
    exclusiveLease: unit.exclusiveLease ?? null,
    softLeases: Array.isArray(unit.softLeases) ? unit.softLeases : [],
  };
};

export const hasPendingLocalChanges = (
  unit: StorageUnit | null | undefined,
): boolean => {
  if (!unit) {
    return false;
  }

  return (
    unit.pendingAction === 'create' ||
    unit.pendingAction === 'update' ||
    unit.isSynced === false
  );
};

export const hasPendingLocalFileDataChanges = (
  unit: StorageUnit | null | undefined,
): boolean => {
  return hasPendingLocalChanges(unit) && unit?.fileData !== undefined;
};

export const hasOfflineFileData = (
  unit: StorageUnit | null | undefined,
): boolean => {
  return !!unit?.fileData;
};

const normalizeComparableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeComparableValue);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizeComparableValue(
          (value as Record<string, unknown>)[key],
        );
        return acc;
      }, {});
  }

  return value ?? null;
};

const getStorageUnitContentFingerprint = (unit: StorageUnit) =>
  normalizeComparableValue({
    name: unit.name,
    parentId: unit.parentId ?? null,
    type: unit.type,
    fileData: unit.fileData ?? null,
    flowsheetMetadata: unit.flowsheetMetadata ?? null,
    status: [...(unit.status ?? [])].sort(),
  });

export const hasRemoteStorageUnitContentChanged = (
  remote: StorageUnit,
  baseline: StorageUnit | null | undefined,
): boolean => {
  if (!baseline) {
    return true;
  }

  return (
    JSON.stringify(getStorageUnitContentFingerprint(remote)) !==
    JSON.stringify(getStorageUnitContentFingerprint(baseline))
  );
};

export const mergeRemoteStorageUnitWithPendingLocal = (
  remote: StorageUnit,
  local: StorageUnit,
): StorageUnit => {
  return {
    ...remote,
    name: local.name,
    parentId: local.parentId,
    type: local.type,
    fileData: remote.fileData ?? local.fileData,
    thumbnail: remote.thumbnail ?? local.thumbnail,
    flowsheetMetadata: local.flowsheetMetadata ?? remote.flowsheetMetadata,
    isSynced: false,
    pendingAction: local.pendingAction ?? 'update',
    syncedAt: local.syncedAt ?? null,
    localUpdatedAt: local.localUpdatedAt ?? null,
  };
};

export const mergeSyncedStorageUnit = (
  remote: StorageUnit,
  local: StorageUnit,
): StorageUnit => {
  return {
    ...remote,
    name: local.name,
    parentId: local.parentId,
    type: local.type,
    fileData: local.fileData ?? remote.fileData,
    thumbnail: local.thumbnail ?? remote.thumbnail,
    flowsheetMetadata: local.flowsheetMetadata ?? remote.flowsheetMetadata,
    updatedAt: remote.updatedAt,
  };
};

export const mergeServerStorageUnitWithSubmittedContent = (
  server: StorageUnit,
  submitted: Pick<StorageUnit, 'fileData' | 'thumbnail' | 'flowsheetMetadata'>,
): StorageUnit => ({
  ...server,
  fileData: server.fileData ?? submitted.fileData,
  thumbnail: server.thumbnail ?? submitted.thumbnail,
  flowsheetMetadata: server.flowsheetMetadata ?? submitted.flowsheetMetadata,
});
